import { createContext, type ReactNode, useMemo } from 'react';
import { type LocationsById, type PartnerTreeNodeV2 } from '@sr/types';
import { useProfileV2 } from '#lib/Profile';
import { useFetchBusinessPartners } from './useFetchBusinessPartners';
import { getDwkeysFromPartnerTrees, getLocationsById } from './utils';

// Create a new context for the partner trees, all DW keys, and loading state
export const LocationsContext = createContext<{
  partnerTrees: PartnerTreeNodeV2[];
  allDwKeys: string[];
  loading: boolean;
  error: boolean;
  locationsById: LocationsById;
} | null>(null);

export const LocationsProvider = ({ children }: { children: ReactNode }) => {
  const { basicProfile } = useProfileV2();

  const {
    data: partnerTrees,
    isLoading: loading,
    isError: error,
  } = useFetchBusinessPartners({ bpIds: basicProfile?.rootPartnerIds ?? [] });

  const locationsById = useMemo(() => getLocationsById(partnerTrees), [partnerTrees]);

  const allDwKeys = useMemo(
    () => (loading ? [] : getDwkeysFromPartnerTrees(partnerTrees)),
    [loading, partnerTrees],
  );

  const contextValue = useMemo(() => {
    return { partnerTrees, allDwKeys, loading, error, locationsById };
  }, [partnerTrees, allDwKeys, loading, error, locationsById]);

  return (
    // Provide the partner trees, all DW keys, and loading state through the context
    <LocationsContext value={contextValue}>{children}</LocationsContext>
  );
};
